import { ReactNode, MouseEvent } from 'react';
import { Badge, CardPrimitive } from '@figma/fpl-components';
import { borderRadius, color as fplColor, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { type ListItemProps, type ListItemButtonProps } from '@mui/material';
import { Box } from '@/UI/Box/Box';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';
import { FlexCenVerSpaceBetween } from '@/UI/styles';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

interface NavigationListItemProps {
  icon: ReactNode;
  label: ReactNode;
  /** Status pill rendered next to the label (e.g. "Beta"). Owned by this component so both the
   * legacy and `fpl-migration` branches render it consistently, rather than being composed ad hoc
   * inside the opaque `label` node by callers. */
  badge?: string;
  /** A badge that decides by itself whether to show, such as `NewBadge` with its end date. Shown in place of `badge`. */
  badgeNode?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
  endIcon?: ReactNode;
  onEndIconClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Accessible name for `endIcon`'s button. Required whenever `endIcon` is passed — enforced at
   * runtime (not by the type, which would otherwise force a discriminated-union prop type that
   * breaks Storybook's `Meta<typeof Component>` inference) via `assertEndIconAriaLabel` below. */
  endIconAriaLabel?: string;
  sx?: ListItemProps['sx'];
  buttonSx?: ListItemButtonProps['sx'];
  'data-testid'?: string;
}

function assertEndIconAriaLabel(endIconAriaLabel: string | undefined): string {
  if (!endIconAriaLabel) {
    throw new Error('NavigationListItem: endIconAriaLabel is required whenever endIcon is provided');
  }
  return endIconAriaLabel;
}

const fplStyles = stylex.create({
  row: {
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    paddingInlineEnd: spacer.spacer16px,
    borderRadius: borderRadius.medium,
    overflow: 'hidden',
    backgroundColor: {
      default: 'transparent',
      ':hover': fplColor.bgHover,
      ':active': fplColor.bgPressed,
    },
    '--end-icon-opacity': {
      default: '0',
      ':hover': '1',
      ':has(:focus-visible)': '1',
    },
    '--end-icon-pointer-events': {
      default: 'none',
      ':hover': 'auto',
      ':has(:focus-visible)': 'auto',
    },
  },
  rowSelected: {
    backgroundColor: {
      default: fplColor.bgSelected,
      ':hover': fplColor.bgSelected,
      ':active': fplColor.bgSelectedPressed,
    },
  },
  mainButton: {
    outlineColor: { default: 'transparent', ':focus-visible': fplColor.borderSelected },
    outlineStyle: { default: 'none', ':focus-visible': 'solid' },
    outlineWidth: { default: 0, ':focus-visible': '1px' },
    outlineOffset: '-1px',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: spacer.spacer8px,
    flexGrow: 1,
    minWidth: 0,
    height: '100%',
    paddingInlineStart: spacer.spacer12px,
    color: fplColor.text,
  },
  contentDisabled: {
    color: fplColor.textDisabled,
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    flexShrink: 0,
    color: 'currentColor',
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacer.spacer8px,
    flexGrow: 1,
    minWidth: 0,
  },
  label: {
    flexGrow: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // A 24px plus in this 32px row, 12px from the right edge.
  rowWithEndIcon: {
    paddingInlineEnd: spacer.spacer12px,
  },
  endIcon: {
    flexShrink: 0,
    opacity: 'var(--end-icon-opacity, 0)',
    pointerEvents: 'var(--end-icon-pointer-events, none)',
  },
  endIconGlyph: {
    display: 'grid',
    width: '100%',
    height: '100%',
    placeItems: 'center',
  },
});

/**
 * A selectable/disabled row for drawer-style navigation lists (highlighted background, emphasized
 * label weight when `selected`). Renders MUI in the legacy branch and FPL primitives (no high-level
 * FPL "nav row" component exists) behind the `fpl-migration` flag.
 */
export function NavigationListItem({
  icon,
  label,
  badge,
  badgeNode,
  selected = false,
  disabled = false,
  onClick,
  endIcon,
  onEndIconClick,
  endIconAriaLabel,
  sx,
  buttonSx,
  'data-testid': testId,
}: NavigationListItemProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled) {
    return (
      <li>
        <CardPrimitive.Root
          data-testid={testId}
          data-fpl-selected={selected || undefined}
          {...stylex.props(
            fplStyles.row,
            selected && fplStyles.rowSelected,
            Boolean(endIcon) && fplStyles.rowWithEndIcon,
          )}
        >
          <CardPrimitive.MainButton
            onClick={onClick}
            disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            aria-current={selected ? 'page' : undefined}
            {...stylex.props(fplStyles.mainButton)}
          />
          <div
            {...stylex.props(
              fplStyles.content,
              selected ? typographyStyles.textBodyMediumStrong : typographyStyles.textBodyMedium,
              disabled && fplStyles.contentDisabled,
            )}
          >
            <span aria-hidden {...stylex.props(fplStyles.iconBox)}>
              {icon}
            </span>
            <CardPrimitive.Title as="span" {...stylex.props(fplStyles.titleGroup)}>
              <span {...stylex.props(fplStyles.label)}>{label}</span>
              {badgeNode ?? (badge && <Badge variant="defaultFilled">{badge}</Badge>)}
            </CardPrimitive.Title>
          </div>
          {endIcon && (
            <CardPrimitive.Interactive {...stylex.props(fplStyles.endIcon)}>
              <AppIconButton
                aria-label={assertEndIconAriaLabel(endIconAriaLabel)}
                disabled={disabled}
                tabIndex={disabled ? -1 : undefined}
                onClick={onEndIconClick}
              >
                <span {...stylex.props(fplStyles.endIconGlyph)}>{endIcon}</span>
              </AppIconButton>
            </CardPrimitive.Interactive>
          )}
        </CardPrimitive.Root>
      </li>
    );
  }

  return (
    <ListItem disablePadding data-testid={testId} sx={{ background: selected ? color.Black92 : '', ...sx }}>
      <ListItemButton onClick={onClick} disabled={disabled} sx={buttonSx}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText
          primary={
            badgeNode || badge ? (
              <FlexCenVerSpaceBetween sx={{ gap: 1 }}>
                <span>{label}</span>
                {badgeNode ?? (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: '6px',
                      height: '16px',
                      borderRadius: '2px',
                      backgroundColor: themeColor.white16_T,
                      color: themeColor.white100,
                      fontSize: '10px',
                      fontWeight: 600,
                      lineHeight: '12px',
                    }}
                  >
                    {badge}
                  </Box>
                )}
              </FlexCenVerSpaceBetween>
            ) : (
              label
            )
          }
          sx={{ '& .MuiListItemText-primary': { fontWeight: selected ? 500 : 400 } }}
        />
        {endIcon && (
          <Box onClick={onEndIconClick} sx={{ p: 1, pr: 0 }}>
            {endIcon}
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
}
